/// <reference path="../typedefs/dash.js" />

import { apiRequest } from 'utils/request'

export const fetchProductionSchedule = ({ requestBody }) =>
  apiRequest({
    method: 'POST',
    uri: '/vehicles/production-schedule/search',
    readOnly: true,
    body: requestBody,
  })

export const getCustomSwitchGraphic = ({ capPartNumber }) =>
  apiRequest(`/custom-switch-graphics/${capPartNumber}`)

/**
 * @param {{ serialNumber: string, isAllium: boolean }} params params
 * @returns {*}
 */
export const getDashboard = ({ serialNumber, isAllium }) =>
  apiRequest(
    `vehicles/${serialNumber}/dashboard${isAllium ? '?isAllium=true' : ''}`
  )

/**
 * @param {{ dashboardType: string, serialNumber: string, isAllium: boolean }} params params
 * @returns {*}
 */
export const getLayoutBlacklist = ({ dashboardType, serialNumber, isAllium }) =>
  apiRequest(
    `/dashboard-layouts/${dashboardType}/blacklist?serialNumber=${serialNumber}${
      isAllium ? '&isAllium=true' : ''
    }`
  )

/**
 * @param {{ vehicleSubType: string, isAllium: boolean }} params params
 * @returns {*}
 */
export const getSwitches = ({ vehicleSubType, isAllium }) =>
  apiRequest(`switches/${vehicleSubType}${isAllium ? '?isAllium=true' : ''}`)

/**
 * @param {string} serialNumber
 * @param {object} requestBody
 * @returns promise
 */
export const saveDashboard = ({ serialNumber, requestBody }) =>
  apiRequest({
    method: 'POST',
    uri: `/vehicles/${serialNumber}/dashboard/update`,
    body: requestBody,
  })

export const getSwitchLinkedReferenceParameter = ({ switchId, serialNumber }) =>
  apiRequest({
    method: 'POST',
    readOnly: true,
    uri: `/reference-parameters/search`,
    body: {
      startIndex: 1,
      count: 100,
      search: '',
      filters: [
        {
          schemaId: 'reference-parameter.in.vehicle',
          operator: 'in',
          value: `${serialNumber}`,
        },
        {
          schemaId: 'reference-parameter.contains.switchId',
          operator: 'contains',
          value: `${switchId}`,
        },
      ],
    },
  })

export const getSwitchLinkedFeatures = ({ switchId, serialNumber }) =>
  apiRequest({
    method: 'POST',
    readOnly: true,
    uri: `/features/search`,
    body: {
      startIndex: 1,
      count: 100,
      search: '',
      filters: [
        {
          schemaId: 'features.in.vehicle',
          operator: 'in',
          value: `${serialNumber}`,
        },
        {
          schemaId: 'features.contains.switchId',
          operator: 'contains',
          value: `${switchId}`,
        },
      ],
    },
  })

export const getSearchedCustomSwitches = ({ searchedString, panelNumber }) =>
  apiRequest(
    `custom-switch-graphics/search?desc=${searchedString}&panelNumber=${panelNumber}`
  )

export const saveCustomSwitchGraphicsDetails = ({
  serialNumber,
  requestBody,
}) =>
  apiRequest({
    method: 'POST',
    uri: `/vehicles/${serialNumber}/dashboard/apply-custom-graphic`,
    body: requestBody,
  })

/**
 * Service function to make HTTP request to update dash layout location.p
 *
 * @typedef {Object} UpdateLayoutLocation
 * @property {string} id
 * @property {SaveDashLayoutLocationPostBody} body
 *
 * @param {UpdateLayoutLocation} object
 * @returns {Promise} Promise
 */
export const updateLayoutLocation = ({ id, body }) =>
  apiRequest({
    method: 'POST',
    uri: `/dashboard-layout/${id}/update`,
    body,
  })

/**
 * Service function to create/save an approved layout.
 *
 * @typedef {Object} SaveApprovedLayout
 * @property {string} serialNumber
 * @property {{ notes: string }} body
 *
 * @param {SaveApprovedLayout} object
 * @returns {Promise} Promise
 */
export const createApprovedLayout = ({ serialNumber, body }) =>
  apiRequest({
    method: 'POST',
    uri: `/vehicles/${serialNumber}/dashboard/layout-approved`,
    body,
  })

export const postReplacedSwitches = ({ serialNumber, body }) =>
  apiRequest({
    method: 'POST',
    uri: `vehicles/${serialNumber}/features/update`,
    body,
  })

/**
 * Fetches dashboard data and its associated blacklist in a single combined
 * request sequence. Used by the RulesUtils developer tool to get all data
 * needed to run the allowed-locations rules engine.
 *
 * Calls getDashboard first, then getLayoutBlacklist using the dashboardType
 * from the dashboard response.
 *
 * NOTE on blacklist shape: the field returned as `blackListsByLocation` is an
 * object keyed by location (`{ [location]: entry }`), suitable for efficient
 * lookup by the rules engine. This differs from the `blockedList` concept used
 * in the main dash reducer (which is a flat string array produced by
 * `createBlockedList`). The raw array form is also returned as `blackList`.
 *
 * @param {{ serialNumber: string }} params
 * @returns {{ components, layouts, databookCodes, dashboardType, blackListsByLocation, blackList }}
 */
export const getRulesUtilsData = async ({ serialNumber }) => {
  const dashResponse = await getDashboard({ serialNumber, isAllium: false })
  const dashData = dashResponse.data

  if (!dashData?.dashboardType) {
    // Mark the error as a read-only GET so defaultErrorHandler treats it as a
    // silent read failure rather than a blocking data-loss mutation modal.
    // This error is thrown outside of apiRequest's .catch enrichment path, so
    // method/readOnly must be set explicitly here.
    const err = new Error(
      `getRulesUtilsData: missing dashboardType in dashboard response for ${serialNumber}`
    )
    err.method = 'GET'
    err.readOnly = true
    throw err
  }

  const blacklistResponse = await getLayoutBlacklist({
    dashboardType: dashData.dashboardType,
    serialNumber,
    isAllium: false,
  })
  // NOTE on serial dependency: getLayoutBlacklist requires dashboardType from
  // the getDashboard response above, so these two calls must run sequentially.
  // If getLayoutBlacklist fails, the already-completed getDashboard result is
  // discarded and the entire operation fails. On retry, both calls repeat.
  // This is intentional for a developer-only tool — the complexity of partial
  // success recovery is not warranted here. If this pattern is ever used in a
  // production flow, consider splitting into two separate Redux actions so each
  // can succeed or fail independently.

  const blackListsByLocation = (blacklistResponse.data || []).reduce(
    (acc, curr) => ({ ...acc, [curr.location]: curr }),
    {}
  )

  return {
    // Wrapped as { data: ... } because apiCall.js reads `asyncFn result.data`
    // when dispatching the success action. This mirrors how apiRequest returns
    // { data: <payload> } from the API envelope. Callers (reducers, selectors)
    // receive the inner object via action.data, not this outer wrapper.
    data: {
      ...dashData,
      // Object keyed by location — useful for O(1) lookup by the rules engine.
      // NOT the same as the `blockedList` string array in the main dash reducer.
      blackListsByLocation,
      blackList: blacklistResponse.data,
    },
  }
}
